import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { TaskRowRich, type RowMember } from "@/components/TaskRowRich";
import { FilterChips } from "@/components/FilterChips";
import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/Skeleton";
import { groupByDue, bucketForDue, type DueGroupKey } from "@/lib/taskGroups";
import type { TaskRow } from "@shared/types";

type SortKey = "due" | "priority" | "project" | "title";

function sortTasks(
  list: TaskRow[],
  key: SortKey,
  projectName: (id: string) => string,
): TaskRow[] {
  const arr = [...list];
  switch (key) {
    case "priority":
      return arr.sort((a, b) => a.priority - b.priority);
    case "project":
      return arr.sort((a, b) =>
        projectName(a.projectId).localeCompare(projectName(b.projectId)),
      );
    case "title":
      return arr.sort((a, b) => a.title.localeCompare(b.title));
    case "due":
    default:
      return arr.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1; // nulls last
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      });
  }
}

function MyTasks() {
  const navigate = useNavigate();
  const mine = useQuery({
    queryKey: queryKeys.meTasks(),
    queryFn: api.myTasks,
  });
  const watching = useQuery({
    queryKey: queryKeys.meWatching(),
    queryFn: api.watching,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });

  const [tab, setTab] = useState<"mine" | "watching">("mine");
  const [search, setSearch] = useState("");
  const [projectId, setProjectId] = useState("all");
  const [priority, setPriority] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sort, setSort] = useState<SortKey>("due");
  // Only keys the user explicitly toggled; everything else uses the data-aware
  // default below.
  const [manualCollapse, setManualCollapse] = useState<
    Map<DueGroupKey, boolean>
  >(() => new Map());

  const projectMap = useMemo(
    () =>
      new Map(
        (projectsQ.data?.projects ?? []).map((p) => [
          p.id,
          { name: p.name, color: p.color ?? null },
        ]),
      ),
    [projectsQ.data],
  );
  const members: RowMember[] = useMemo(
    () =>
      (teamQ.data?.members ?? []).map((m) => ({
        id: m.id,
        name: m.name ?? "Member",
        image: m.image ?? null,
      })),
    [teamQ.data],
  );
  const now = useMemo(() => new Date(), []);
  const projectName = (id: string) => projectMap.get(id)?.name ?? id;

  const active = tab === "mine" ? mine : watching;

  const groups = useMemo(() => {
    let list = active.data?.tasks ?? [];
    if (!showCompleted) list = list.filter((t) => t.status !== "done");
    if (projectId !== "all")
      list = list.filter((t) => t.projectId === projectId);
    if (priority !== "all")
      list = list.filter((t) => String(t.priority) === priority);
    if (overdueOnly)
      list = list.filter((t) => bucketForDue(t.dueDate, now) === "overdue");
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((t) => t.title.toLowerCase().includes(q));
    return groupByDue(sortTasks(list, sort, projectName), now);
  }, [
    active.data,
    showCompleted,
    projectId,
    priority,
    overdueOnly,
    search,
    sort,
    now,
    // projectName closes over projectMap
    projectMap,
  ]);

  const visibleCount = groups.reduce((n, g) => n + g.tasks.length, 0);

  // Collapse the long-tail groups (Later / No due date) by default — but only
  // when there's something urgent above them. If they're the only groups, keep
  // them open so the page never looks empty.
  const hasUrgent = groups.some(
    (g) =>
      (g.key === "overdue" || g.key === "today" || g.key === "week") &&
      g.tasks.length > 0,
  );
  const defaultCollapsed = (key: DueGroupKey) =>
    (key === "later" || key === "nodate") && hasUrgent;
  const isCollapsed = (key: DueGroupKey) =>
    manualCollapse.has(key) ? manualCollapse.get(key)! : defaultCollapsed(key);

  function toggleGroup(key: DueGroupKey) {
    setManualCollapse((prev) => {
      const next = new Map(prev);
      next.set(key, !isCollapsed(key));
      return next;
    });
  }

  return (
    <div className="pg-wrap pg-mytasks">
      <div className="pg-head">
        <h1>My Tasks</h1>
        {visibleCount > 0 && <span className="count">{visibleCount}</span>}
      </div>

      {/* Tabs */}
      <div className="mt-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "mine"}
          className={`mt-tab${tab === "mine" ? " active" : ""}`}
          onClick={() => setTab("mine")}
        >
          Mine{" "}
          <span className="mt-tab-count">{mine.data?.tasks.length ?? 0}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "watching"}
          className={`mt-tab${tab === "watching" ? " active" : ""}`}
          onClick={() => setTab("watching")}
        >
          Watching{" "}
          <span className="mt-tab-count">
            {watching.data?.tasks.length ?? 0}
          </span>
        </button>
      </div>

      {/* Toolbar */}
      <div className="mt-toolbar">
        <input
          type="search"
          className="input mt-search"
          placeholder="Search my tasks…"
          aria-label="Search my tasks"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="select"
          aria-label="Filter by project"
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
        >
          <option value="all">All projects</option>
          {(projectsQ.data?.projects ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <FilterChips
          options={[
            { value: "all", label: "Any priority" },
            { value: "1", label: "Urgent" },
            { value: "2", label: "High" },
            { value: "3", label: "Medium" },
            { value: "4", label: "Low" },
          ]}
          value={priority}
          onChange={setPriority}
        />
        <button
          type="button"
          className={`fchip${overdueOnly ? " on" : ""}`}
          aria-pressed={overdueOnly}
          onClick={() => setOverdueOnly((v) => !v)}
        >
          Overdue only
        </button>
        <button
          type="button"
          className={`fchip${showCompleted ? " on" : ""}`}
          aria-pressed={showCompleted}
          onClick={() => setShowCompleted((v) => !v)}
        >
          Show completed
        </button>
        <div className="spacer" />
        <label className="mt-sort">
          Sort
          <select
            className="select"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
          >
            <option value="due">Due date</option>
            <option value="priority">Priority</option>
            <option value="project">Project</option>
            <option value="title">Title</option>
          </select>
        </label>
      </div>

      {/* List */}
      {active.isLoading ? (
        <div style={{ padding: 16 }}>
          <Skeleton count={8} height={44} />
        </div>
      ) : active.isError ? (
        <p className="t-sm t-muted" style={{ padding: 16 }}>
          Couldn&rsquo;t load these tasks. Refresh to try again.
        </p>
      ) : visibleCount === 0 ? (
        <EmptyState
          icon={tab === "mine" ? "check" : "star"}
          title={
            tab === "mine" ? "Nothing on your plate." : "Nothing here yet."
          }
          caption={
            tab === "mine"
              ? "Pick up something from the team backlog."
              : "Tasks you comment on appear here."
          }
          actionLabel={tab === "mine" ? "Browse projects" : undefined}
          onAction={
            tab === "mine" ? () => navigate({ to: "/projects" }) : undefined
          }
        />
      ) : (
        <div className="mt-groups">
          {groups
            .filter((g) => g.tasks.length > 0)
            .map((g) => {
              const groupCollapsed = isCollapsed(g.key);
              return (
                <section key={g.key} className="mt-group">
                  <button
                    type="button"
                    className="mt-group-head"
                    aria-expanded={!groupCollapsed}
                    onClick={() => toggleGroup(g.key)}
                  >
                    <span
                      className={`mt-caret${groupCollapsed ? "" : " open"}`}
                    >
                      ▸
                    </span>
                    {g.label}
                    <span className="mt-group-count">{g.tasks.length}</span>
                  </button>
                  {!groupCollapsed && (
                    <div className="panel mt-group-body">
                      {g.tasks.map((t) => (
                        <TaskRowRich
                          key={t.id}
                          task={t}
                          projectName={projectMap.get(t.projectId)?.name}
                          projectColor={projectMap.get(t.projectId)?.color}
                          members={members}
                          canAct={tab === "mine"}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/my-tasks")({ component: MyTasks });
